import React, { useState, useEffect, useCallback, useRef } from 'react';
import client from '../api/client';
import { parseApiDate } from '../utils/dateTime';
import { subscribeCacheRefreshed } from '../utils/cacheEvents';
import { useAuth } from '../contexts/AuthContext';
import './FreshnessBanner.css';

const ALL_DATA_TYPES = [
  // 'groups' was retired 2026-05; see backend DataTypeNames for the
  // rationale. The remaining entries match the registered cacheable
  // types — keep in sync with backend/Services/DataCacheService.cs.
  'referrals', 'vitalrounds', 'messages', 'logins', 'studies',
  'group-referrals', 'faxes', 'fax-recipients', 'study-details',
  'referral-events',
];
const AUTO_COLLAPSE_MS = 3500;

function formatRelative(iso) {
  if (!iso) return 'never';
  const date = parseApiDate(iso);
  if (!date || Number.isNaN(date.getTime())) return 'unknown';
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

function formatRelativeLong(iso) {
  if (!iso) return 'never';
  const date = parseApiDate(iso);
  if (!date || Number.isNaN(date.getTime())) return 'unknown';
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return date.toLocaleDateString();
}

function FreshnessBanner() {
  const { hasPermission } = useAuth();
  const canRefresh = hasPermission('admin.data-sources', true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [veRefreshing, setVeRefreshing] = useState(false);
  const [sfRefreshing, setSfRefreshing] = useState(false);
  const [veMessage, setVeMessage] = useState('');
  const [sfMessage, setSfMessage] = useState('');
  const [expanded, setExpanded] = useState(true);
  const [sticky, setSticky] = useState(false);
  const collapseTimerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await client.get('/api/cache/freshness');
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    const unsub = subscribeCacheRefreshed(() => { load(); });
    return () => {
      clearInterval(id);
      unsub();
    };
  }, [load]);

  useEffect(() => {
    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = null;
    }

    if (sticky) {
      setExpanded(true);
      return;
    }

    const busy = loading || veRefreshing || sfRefreshing;
    if (busy) {
      setExpanded(true);
      return;
    }

    collapseTimerRef.current = setTimeout(() => {
      setExpanded(false);
    }, AUTO_COLLAPSE_MS);

    return () => {
      if (collapseTimerRef.current) {
        clearTimeout(collapseTimerRef.current);
        collapseTimerRef.current = null;
      }
    };
  }, [loading, veRefreshing, sfRefreshing, sticky]);

  const refreshVitalEngine = async () => {
    setVeRefreshing(true);
    setVeMessage('');
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);

      const res = await client.post('/api/cache/refresh', {
        dataTypes: ALL_DATA_TYPES,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        mode: 'smart',
      });
      const added = (res.data?.results || []).reduce((sum, r) => sum + (r.recordCount || 0), 0);
      setVeMessage(added > 0 ? `+${added} new record${added === 1 ? '' : 's'}` : 'Up to date');
      await load();
    } catch (err) {
      setVeMessage(err.response?.data?.error || 'Refresh failed');
    } finally {
      setVeRefreshing(false);
      setTimeout(() => setVeMessage(''), 6000);
    }
  };

  const refreshFacilities = async () => {
    setSfRefreshing(true);
    setSfMessage('');
    try {
      const res = await client.post('/api/cache/refresh-facilities');
      setSfMessage(`${res.data?.count ?? '?'} facilities reloaded`);
      await load();
    } catch (err) {
      setSfMessage(err.response?.data?.error || 'Refresh failed');
    } finally {
      setSfRefreshing(false);
      setTimeout(() => setSfMessage(''), 6000);
    }
  };

  const handleToggle = () => {
    if (expanded) {
      setExpanded(false);
      setSticky(false);
    } else {
      setExpanded(true);
      setSticky(true);
    }
  };

  if (loading && !data) return null;

  const veDataTypes = data?.dataTypes || [];
  const veTypeSummary = veDataTypes
    .filter(dt => dt.lastRefreshed)
    .map(dt => `${dt.dataType}: ${formatRelativeLong(dt.lastRefreshed)} (${dt.recordCount})`)
    .join('\n');

  if (!expanded) {
    return (
      <div
        className="freshness-banner freshness-collapsed"
        role="button"
        tabIndex={0}
        onClick={handleToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle(); }
        }}
        title="Click to expand data freshness details"
      >
        <span className="freshness-mini-spacer" />
        <span className="freshness-mini-item">
          <span className={`freshness-dot ${veRefreshing ? 'spinning' : ''}`} aria-hidden />
          <span className="freshness-mini-label">VitalEngine Data</span>
          <span className="freshness-mini-time">{formatRelative(data?.lastRefreshedAt)}</span>
        </span>
        <span className="freshness-mini-sep" aria-hidden>·</span>
        <span className="freshness-mini-item">
          <span className={`freshness-dot sf ${sfRefreshing ? 'spinning' : ''}`} aria-hidden />
          <span className="freshness-mini-label">Salesforce</span>
          <span className="freshness-mini-time">{formatRelative(data?.facilitiesLastRefreshedAt)}</span>
        </span>
        <span className="freshness-mini-spacer" />
        <span className="freshness-mini-chevron" aria-hidden>▾</span>
      </div>
    );
  }

  return (
    <div className="freshness-banner">
      <div className="freshness-header">
        <div className="freshness-sources">
          <div className={`freshness-pill ${veRefreshing ? 'refreshing' : ''}`}>
            <span className={`freshness-dot ${veRefreshing ? 'spinning' : ''}`} aria-hidden />
            <div className="freshness-pill-body">
              <div className="freshness-pill-title">
                VitalEngine Data
                <span className="freshness-pill-badge">incremental</span>
              </div>
              <div className="freshness-pill-meta" title={veTypeSummary || undefined}>
                {veRefreshing ? (
                  <em>Pulling new records…</em>
                ) : (
                  <>Last refreshed <strong>{formatRelativeLong(data?.lastRefreshedAt)}</strong></>
                )}
                {veMessage && <span className="freshness-inline-msg">{veMessage}</span>}
              </div>
            </div>
            {canRefresh && (
              <button
                className="freshness-pill-btn"
                onClick={refreshVitalEngine}
                disabled={veRefreshing}
              >
                {veRefreshing ? 'Refreshing…' : 'Refresh'}
              </button>
            )}
          </div>

          <div className={`freshness-pill ${sfRefreshing ? 'refreshing' : ''}`}>
            <span className={`freshness-dot sf ${sfRefreshing ? 'spinning' : ''}`} aria-hidden />
            <div className="freshness-pill-body">
              <div className="freshness-pill-title">
                Salesforce facilities
                <span className="freshness-pill-badge alt">full reload</span>
              </div>
              <div className="freshness-pill-meta">
                {sfRefreshing ? (
                  <em>Reloading facility hierarchy…</em>
                ) : data?.salesforceSyncEnabled === false ? (
                  <>Using cached facilities only — Salesforce sync paused</>
                ) : (
                  <>Last refreshed <strong>{formatRelativeLong(data?.facilitiesLastRefreshedAt)}</strong></>
                )}
                {sfMessage && <span className="freshness-inline-msg">{sfMessage}</span>}
              </div>
            </div>
            {canRefresh && data?.salesforceSyncEnabled !== false && (
              <button
                className="freshness-pill-btn"
                onClick={refreshFacilities}
                disabled={sfRefreshing}
              >
                {sfRefreshing ? 'Refreshing…' : 'Refresh'}
              </button>
            )}
          </div>
        </div>

        <button
          className="freshness-collapse-btn"
          onClick={handleToggle}
          title="Collapse"
          aria-label="Collapse freshness details"
        >
          ▴
        </button>
      </div>

      <div className="freshness-footnote">
        Smart refresh pulls only new records since the last refresh and stores them anonymized
        in the local database. The upstream VitalEngine reporting DB refreshes nightly at 2 AM CT,
        so data here can't be newer than that.
      </div>
    </div>
  );
}

export default FreshnessBanner;
